import { useMemo, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { Button, Input } from '@repo/videa-components';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { endpoints } from '@/react-query/endpoints';
import { usePreferences } from '@/react-query/usePreferences';
import { useCreateStandalonePatientVisit } from '@/react-query/videa-notes/useCreateStandalonePatientVisit';
import { isNameValid, isValidDOB } from '@/utils/validation';

import { selectDateAtom } from '../../patientList/atoms';

interface QuickNotePatientFormProps {
  onPatientCreated: (visitData: {
    id: string;
    firstName: string;
    lastName: string | null;
    sourcePatientId: string | null;
    dateOfBirth: Date | null;
    appointmentDate: Date;
    appointmentStartTime: string;
  }) => void;
  onCancel: () => void;
}

export const QuickNotePatientForm = ({
  onPatientCreated,
  onCancel,
}: QuickNotePatientFormProps) => {
  // atoms
  const selectedDate = useAtomValue(selectDateAtom);

  // Helper function to get the nearest half-hour time
  const getNearestHalfHour = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const hours = now.getHours();

    // Round to nearest half-hour
    const roundedMinutes = minutes < 15 ? 0 : minutes < 45 ? 30 : 0;
    const roundedHours = minutes >= 45 ? (hours + 1) % 24 : hours;

    return `${roundedHours.toString().padStart(2, '0')}:${roundedMinutes
      .toString()
      .padStart(2, '0')}`;
  };

  // local state
  const [patientFormData, setPatientFormData] = useState({
    firstName: '',
    lastName: undefined as string | undefined,
    fullName: '',
    sourcePatientId: undefined as string | undefined,
    dateOfBirth: undefined as Date | undefined,
    dobDay: '',
    dobMonth: '',
    dobYear: '',
    appointmentDate: selectedDate || new Date(),
    appointmentTime: getNearestHalfHour(), // Nearest half-hour time
  });

  // hooks
  const { data: preferences } = usePreferences();
  const createStandalonePatientVisit = useCreateStandalonePatientVisit();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!patientFormData.fullName.trim()) {
      return;
    }

    // Split the full name at the first space
    const fullName = patientFormData.fullName.trim();
    const spaceIndex = fullName.indexOf(' ');
    const firstName =
      spaceIndex > 0 ? fullName.substring(0, spaceIndex) : fullName;
    const lastName =
      spaceIndex > 0 ? fullName.substring(spaceIndex + 1) : undefined;

    // Validate that at least the first name is provided
    if (!firstName) {
      // This should not happen due to button validation, but adding as safety
      return;
    }

    try {
      // Construct date of birth from separate fields
      let dateOfBirth: Date | null = null;
      if (
        patientFormData.dobDay &&
        patientFormData.dobMonth &&
        patientFormData.dobYear
      ) {
        const year = parseInt(patientFormData.dobYear);
        const month = parseInt(patientFormData.dobMonth) - 1; // Month is 0-indexed
        const day = parseInt(patientFormData.dobDay);

        // Validate the date
        const testDate = new Date(year, month, day);
        if (
          testDate.getFullYear() === year &&
          testDate.getMonth() === month &&
          testDate.getDate() === day
        ) {
          dateOfBirth = testDate;
        }
      }

      // Parse the time from dropdown (already in 24-hour format)
      const [hours, minutes] = patientFormData.appointmentTime
        .split(':')
        .map(Number);

      // Create separate date and time for the API
      const appointmentDate = new Date(patientFormData.appointmentDate);

      // build local date + time using constructor
      const localDateTime = new Date(
        appointmentDate.getFullYear(),
        appointmentDate.getMonth(),
        appointmentDate.getDate(),
        hours,
        minutes,
        0, // seconds
        0, // milliseconds
      );

      // convert to UTC time string (HH:MM:SS)
      const appointmentStartTimeUtc = localDateTime
        .toISOString()
        .split('T')[1]
        .slice(0, 8);

      const sourcePatientId =
        patientFormData.sourcePatientId?.trim() || undefined;

      const submitData = {
        firstName: firstName.trim(),
        lastName: lastName?.trim() || undefined,
        sourcePatientId,
        dateOfBirth: dateOfBirth?.toISOString(), // ISO datetime string
        appointmentDate: appointmentDate.toISOString(), // YYYY-MM-DD
        appointmentStartTime: appointmentStartTimeUtc, // HH:MM:SS in UTC
        appointmentOn: localDateTime.toISOString(),
      };

      // Create the standalone patient visit
      const visitData =
        await createStandalonePatientVisit.mutateAsync(submitData);

      // Invalidate queries to refresh the table
      queryClient.invalidateQueries([
        endpoints.ambient.standalonePatientVisits,
      ]);
      queryClient.invalidateQueries([endpoints.appointments]);

      onPatientCreated(visitData);
    } catch (error) {
      // Handle error silently or show user-friendly message
      if (error && typeof error === 'object' && 'response' in error) {
        // Handle API errors appropriately
      }
    }
  };

  // Derived state: DOB is valid if it passes validation OR if all fields are empty (optional)
  const isDOBValid = useMemo(
    () =>
      isValidDOB(
        patientFormData.dobDay,
        patientFormData.dobMonth,
        patientFormData.dobYear,
        { isOptional: true },
      ),
    [patientFormData.dobDay, patientFormData.dobMonth, patientFormData.dobYear],
  );

  return (
    <div className="vai-flex vai-flex-col vai-space-y-6 vai-font-inter">
      <div className="vai-text-left">
        <p className="vai-font-inter vai-text-gray-600 vai-mt-2">
          <Trans>Enter patient name to start recording a note</Trans>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="vai-space-y-4">
        <div className="vai-flex vai-gap-4">
          <div className="vai-flex-1">
            <label
              htmlFor="fullName"
              className="vai-block vai-font-inter vai-text-sm vai-font-medium vai-text-gray-700 vai-mb-1"
            >
              <Trans>Full Name *</Trans>
            </label>
            <Input
              id="fullName"
              type="text"
              value={patientFormData.fullName}
              onChange={(e) => {
                setPatientFormData((prev) => ({
                  ...prev,
                  fullName: e.target.value,
                }));
              }}
              placeholder="Enter first and last name (e.g., John Doe)"
              required
              className="vai-w-full vai-font-inter"
            />
            {preferences?.enableQuickNotesPatientId && (
              <>
                <label
                  htmlFor="sourcePatientId"
                  className="vai-block vai-font-inter vai-text-sm vai-font-medium vai-text-gray-700 vai-pt-2 vai-mb-1"
                >
                  <Trans>Patient ID</Trans>
                </label>
                <Input
                  id="sourcePatientId"
                  type="text"
                  value={patientFormData.sourcePatientId}
                  onChange={(e) => {
                    setPatientFormData((prev) => ({
                      ...prev,
                      sourcePatientId: e.target.value,
                    }));
                  }}
                  placeholder="Enter patient identification number"
                  required={false}
                  className="vai-w-full vai-font-inter"
                />
              </>
            )}
          </div>
        </div>

        <div className="vai-flex vai-justify-end vai-space-x-3 vai-pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="vai-px-6 vai-font-inter"
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            type="submit"
            disabled={
              !isNameValid(patientFormData.fullName) ||
              !isDOBValid ||
              createStandalonePatientVisit.isLoading
            }
            className="vai-px-6 vai-font-inter"
          >
            {createStandalonePatientVisit.isLoading ? (
              <Trans>Creating...</Trans>
            ) : (
              <Trans>Continue</Trans>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};
